import axiosInstance from "@/lib/axiosInstance";
import { formatDate, MessageTypeColors, MessageTypes } from "@/lib/utils"
import { DoneAll } from "@mui/icons-material";
import { Box, Chip, Collapse, Grid, IconButton, List, ListItem, ListItemSecondaryAction, ListItemText, Pagination, Stack, Tooltip, Typography } from "@mui/material"
import { Pencil, Trash2 } from "lucide-react";
import React, { useState } from "react";
import CustomDialog from "./material/CustomDialog";

const MessageCard = ({ message, fetchMessages, limit, currentUserId, read, toast, page, setPage, onEdit, userData }: any) => {

    const [expandedId, setExpandedId] = useState<string | null>(null);
    const [openDeleteModal, setOpenDeleteModal] = useState<any>(null);

    const handleToggle = (id: string) => {
        setExpandedId((prev) => (prev === id ? null : id));
    };

    const deleteMessage = async (id: string) => {
        try {
            const { data } = await axiosInstance.delete(`/api/messages/${id}`);
            if (data.success) {
                toast({
                    variant: "success",
                    title: "Success!",
                    description: "Message deleted successfully",
                })
                fetchMessages();
            } else {
                toast({
                    variant: "destructive",
                    title: "Error!",
                    description: data.message || "Something went wrong",
                })
            }
        } catch (error: any) {
            toast({
                variant: "destructive",
                title: "Error!",
                description: error?.response?.data?.message || "Something went wrong",
            })
        }
    }

    return (
        <Box>

            <List dense>
                {(message?.data ?? []).map((m: any) => {
                    const isUnread = userData?.userType !== "admin" && !(m.readBy || []).includes(currentUserId);
                    const isExpanded = expandedId === m._id;
                    return (
                        <React.Fragment key={m._id}>
                            <ListItem
                                onClick={() => handleToggle(m._id)}
                                sx={{
                                    bgcolor: isUnread ? 'action.hover' : 'transparent',
                                    borderRadius: 1, border: '1px solid',
                                    borderColor: 'divider',
                                    mb: 0.5,
                                    cursor: "pointer",
                                    '&:hover': {
                                        bgcolor: 'action.hover'
                                    }
                                }}>
                                <Grid container spacing={2} sx={{ width: "100%" }}>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <ListItemText
                                            primaryTypographyProps={{
                                                fontWeight: 700,
                                                variant: "body1",
                                            }}
                                            primary={m.title}
                                            secondary={`From: ${m.fromUser?.firstName} ${m.fromUser?.lastName} • ${formatDate(m.createdAt)}`}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <Chip
                                            label={MessageTypes[m.type]}
                                            color={MessageTypeColors[m.type] || "primary"}
                                            sx={{ width: { xs: "100%", sm: 150 } }}
                                        />
                                    </Grid>
                                </Grid>
                                <ListItemSecondaryAction>
                                    {isUnread && (
                                        <Tooltip title="Mark as read" placement="top">
                                            <IconButton onClick={() => read.mutate(m._id)} color="success"><DoneAll /></IconButton>
                                        </Tooltip>
                                    )}
                                    <Tooltip title="Edit Message" placement="top">
                                        <IconButton onClick={() => onEdit(m)} color="info"><Pencil /></IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete MEssage" placement="top">
                                        <IconButton onClick={() => setOpenDeleteModal(m)} color="error"><Trash2 /></IconButton>
                                    </Tooltip>
                                </ListItemSecondaryAction>
                            </ListItem>

                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                <Box
                                    sx={{
                                        px: 3,
                                        py: 2,
                                        bgcolor: "background.paper",
                                        borderLeft: "2px solid",
                                        borderColor: "divider",
                                        mb: 1
                                    }}
                                >
                                    <Typography variant="body1" gutterBottom>{m.body}</Typography>

                                    <Typography variant="subtitle2">Recipients:</Typography>
                                    {m.toUsers?.map((u: any) => (
                                        <Typography key={u._id} variant="body2">
                                            {u.firstName} {u.lastName} ({u.email})
                                        </Typography>
                                    ))}

                                    <Typography variant="subtitle2" mt={1}>
                                        Read By:
                                    </Typography>
                                    {m.readBy?.length
                                        ? m.readBy.map((id: string) => (
                                            <Typography key={id} variant="body2">
                                                {id}
                                            </Typography>
                                        ))
                                        : <Typography variant="body2">No one has read yet.</Typography>}
                                </Box>
                            </Collapse>
                        </React.Fragment>
                    );
                })}
            </List>
            {
                message?.data?.length === 0 && (
                    <Typography variant="body1" textAlign={"center"}>No messages found</Typography>
                )
            }

            <Stack alignItems="center" mt={1}>
                <Pagination
                    page={page + 1}
                    count={Math.ceil((message?.counts || 0) / limit) || 1}
                    onChange={(_e, p) => setPage(p)}
                    color="primary"
                />

            </Stack>
            {
                openDeleteModal && (
                    <CustomDialog
                        title="Delete Message"
                        content="Are you sure you want to delete this message?"
                        onClose={() => setOpenDeleteModal(null)}
                        onConfirm={() => deleteMessage(openDeleteModal?._id)}
                    />
                )
            }
        </Box>
    )
}

export default MessageCard